import { useSelector } from "react-redux"
import { useState } from "react"
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, Button, Text, Stack, useDisclosure } from "@chakra-ui/react"
import PlanSelectionView from "./PlanSelectionView"
import { hasLimitedValiditySubscription, isLegacyUser } from "../../utilities/journeyCreateHelper";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "@chakra-ui/react";
import { ModalCloseButton } from "@chakra-ui/modal";
import { WrapItem } from "@chakra-ui/layout";
import { Tooltip } from "@chakra-ui/tooltip";
import { useDispatch } from "react-redux";
import { updateUserPreferences } from "../../features/auth/authSlice";
import HomeCountrySelector from '../../components/HomeCountrySelector';


function FreeRestrictedNewUserOnboardingModal() {

  const { user } = useSelector((state) => state.auth)

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const [currentPage, setCurrentPage] = useState(1);

  const pages = [
    <>
      <Text>Thank you for becoming a TrainLookout Member. We're excited to have you on board!</Text>
      <Text>Our service is paid, enabling continual improvements and updates to our dataset. We are fully advertising-free for all, and your data remains yours.</Text>

      <Text>No commitment, cancel anytime & 14-day money back guarantee, with no questions asked.</Text>
    </>,
    // Page 2: Plan view
    <>
      <PlanSelectionView />
    </>,
  ];

  const goToNextPage = () => {
    if (currentPage < pages.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isClosable={false} closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Welcome to TrainLookout Beta!</ModalHeader>
        <ModalBody minHeight='200px'>
          <Stack spacing={1}>
            {pages[currentPage - 1]}
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Stack direction='row' spacing={3} flexGrow={1} justifyContent='flex-end'>
            {currentPage > 1 && (
              <Button colorScheme='gray' onClick={goToPreviousPage} ml={3}>
                Back
              </Button>
            )}

            {currentPage < pages.length && (
              <Button colorScheme='blue' onClick={goToNextPage} minWidth='125px'>
                Next
              </Button>
            )}
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function RestrictedPurchasedSubscriptionOnboardingModal() {

  const { user } = useSelector((state) => state.auth);

  const { updatePreferencesRequest } = useSelector((state) => state.auth);

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState(undefined);

  const dispatch = useDispatch();

  const getStartedButtonPressed = () => {
    if (selectedCountry) {
      dispatch(updateUserPreferences({ homeCountryCode: selectedCountry.code }));
    }
  };

  useEffect(() => {
    window.history.replaceState({}, document.title, window.location.pathname)
  }, [updatePreferencesRequest.isSuccess, onClose]);

  // Handling page navigation
  const pages = [
    // Page 1: Greeting & home country select
    <>
      <Text>
        Thank you for purchasing an All-Country Pass and for your valuable support.{' '}
        {hasLimitedValiditySubscription(user) && 'Your non-renewable pass is now active.'}
        {!hasLimitedValiditySubscription(user) && 'Your subscription is now active.'}
      </Text>
      <Text>In order to access your map and start adding journeys, please select your home country:</Text>
      <HomeCountrySelector
        setSelectedCountry={(country) => setSelectedCountry(country)}
        preselectedCountry={selectedCountry ? selectedCountry.name : undefined}
      />
      <Text fontSize='sm'>This can be your home country or where you travel most often.</Text>
    </>,
    // Page 2: How to (future) & contact info
    <>
      <Text>Quick tip: Journeys can be added manually, or automatically through an email forward of the booking confirmation.{' '}
        <Link href='/blog/introducing-email-autoimport' target="_blank">
          <Text color='blue.600' as='u'>Click here to learn more about Email Auto-Import.</Text>
        </Link>
      </Text>
      { !hasLimitedValiditySubscription && (
        <Text>You may also change the default map background to one of the 6 different options in{' '}
        <Link href='/preferences' target="_blank">
          <Text color='blue.600' as='u'>Account & Preferences</Text>
        </Link>.
      </Text>
      )}
      {/* <Text>
        You can get started right away, or check out our <Link href='/explore/myjourneys' target="_blank">
          <Text color='blue.600' as='u'>Quick Start Guide</Text>
        </Link> to learn more.
      </Text> */}
      <Text>
        Your feedback is key to helping improve TrainLookout.
        We are always reachable via social media & email for your suggestions, queries, or any support you might need.{' '}
        <Link href='/contact' target="_blank">
          <Text color='blue.600' as='u'>Do not hesitate to reach out!</Text>
        </Link>
      </Text>
    </>,
  ];

  const goToNextPage = () => {
    if (currentPage < pages.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isClosable={false} closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Welcome to TrainLookout Beta!</ModalHeader>
        <ModalBody minHeight='290px'>
          <Stack spacing={1}>
            {pages[currentPage - 1]}
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Stack direction='row' spacing={3} flexGrow={1} justifyContent='flex-end'>
            {currentPage > 1 && (
              <Button colorScheme='gray' onClick={goToPreviousPage} ml={3}>
                Back
              </Button>
            )}

            {currentPage < pages.length && (
              <WrapItem>
                <Tooltip
                  label='Please select your home country before continuing'
                  closeDelay={500}
                  isDisabled={selectedCountry}
                >
                  <Button colorScheme='blue' onClick={goToNextPage} isDisabled={!selectedCountry} minWidth='125px'>
                    Next
                  </Button>
                </Tooltip>
              </WrapItem>
            )}
            {currentPage === pages.length && (
              <Button
                colorScheme='blue'
                onClick={getStartedButtonPressed}
                isDisabled={!selectedCountry}
                isLoading={updatePreferencesRequest.isLoading}
                minWidth='125px'
              >
                Get started!
              </Button>
            )}
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function NewSubscriptionAcknowledgementModal() {

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true })

  useEffect(() => {
    window.history.replaceState({}, document.title, window.location.pathname)
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Subscription successful!</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack>
            <Text>
              Thank you for purchasing an All-Country Pass and for your valuable support. Your subscription is now active, enabling you to create unlimited journeys worldwide.
            </Text>
            <Text>
              You may now access our comprehensive <Link href='/myjourneys/insights'><Text color='blue.600' as='u'>Insights</Text></Link> and personalize your experience by selecting from six unique map backgrounds in <Link href='/preferences'><Text color='blue.600' as='u'>Account & Preferences</Text></Link>.
            </Text>
            <Text>
              We are always available for your suggestions, queries, or any support you might need. Please <Link href='/contact' target="_blank"><Text color='blue.600' as='u'>do not hesitate to reach out</Text></Link> via social media or email.
            </Text>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='blue' mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

function LegacyNewFreeUserOnboardingView() {

  const { updatePreferencesRequest } = useSelector((state) => state.auth);

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState(undefined);

  const dispatch = useDispatch();

  const getStartedButtonPressed = () => {
    if (selectedCountry) {
      dispatch(updateUserPreferences({ homeCountryCode: selectedCountry.code }));
    }
  };

  useEffect(() => {
    if (updatePreferencesRequest.isSuccess) {
      onClose();
    }
  }, [updatePreferencesRequest.isSuccess, onClose]);

  // Handling page navigation
  const pages = [
    // Page 1: Greeting & home country select
    <>
      <Text>Thank you for becoming a TrainLookout Member. We're excited to have you on board!</Text>
      <Text>In order to access your map and start adding journeys, please select your home country:</Text>
      <HomeCountrySelector
        setSelectedCountry={(country) => setSelectedCountry(country)}
        preselectedCountry={selectedCountry ? selectedCountry.name : undefined}
      />
      <Text fontSize='sm'>This can be your home country or where you travel most often.</Text>
    </>,
    // Page 2: Pricing info
    <>
      <Text>
        Free users are limited to journeys within, to & from the selected home country.
      </Text>
      <Text>
        For international access and additional features, consider our All-Country Pass.{' '}
        <Link href='/pricing' target="_blank">
          <Text color='blue.600' as='u'>Find out more about our plans and pricing.</Text>
        </Link>
      </Text>
      <Text>
        TrainLookout operates advertising-free for all, relying on the support of our paid members to keep our dataset updated and maintain our service.
      </Text>
    </>,
    // Page 3: How to (future) & contact info
    <>
      <Text>Quick tip: Journeys can be added manually, or automatically through an email forward of the booking confirmation.{' '}
        <Link href='/blog/introducing-email-autoimport' target="_blank">
          <Text color='blue.600' as='u'>Click here to learn more about Email Auto-Import.</Text>
        </Link>
      </Text>
      {/* <Text>
          You can get started right away, or check out our <Link href='/explore/myjourneys' target="_blank">
            <Text color='blue.600' as='u'>Quick Start Guide</Text>
          </Link> to learn more.
        </Text> */}
      <Text>
        Your feedback is key to helping improve TrainLookout.
        We are always reachable via social media & email for your suggestions, queries, or any support you might need.{' '}
        <Link href='/contact' target="_blank">
          <Text color='blue.600' as='u'>Do not hesitate to reach out!</Text>
        </Link>
      </Text>
    </>,
  ];

  const goToNextPage = () => {
    if (currentPage < pages.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isClosable={false} closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Welcome to TrainLookout Beta!</ModalHeader>
        <ModalBody minHeight='290px'>
          <Stack spacing={1}>
            {pages[currentPage - 1]}
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Stack direction='row' spacing={3} flexGrow={1} justifyContent='flex-end'>
            {currentPage > 1 && (
              <Button colorScheme='gray' onClick={goToPreviousPage} ml={3}>
                Back
              </Button>
            )}

            {currentPage < pages.length && (
              <WrapItem>
                <Tooltip
                  label='Please select your home country before continuing'
                  closeDelay={500}
                  isDisabled={selectedCountry}
                >
                  <Button colorScheme='blue' onClick={goToNextPage} isDisabled={!selectedCountry} minWidth='125px'>
                    Next
                  </Button>
                </Tooltip>
              </WrapItem>
            )}
            {currentPage === pages.length && (
              <Button
                colorScheme='blue'
                onClick={getStartedButtonPressed}
                isDisabled={!selectedCountry}
                isLoading={updatePreferencesRequest.isLoading}
                minWidth='125px'
              >
                Get started!
              </Button>
            )}
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function OnboardingViews() {

  const { user } = useSelector((state) => state.auth)
  const { journeys } = useSelector((state) => state.journeys)

  const [searchParams] = useSearchParams()
  const [isOnboardedSubscribing, setIsOnboardedSubscribing] = useState(false)

  // For onboarded users who are subscribing
  useEffect(() => {
    if (searchParams.get('newACP') === 'true' && !(searchParams.get('onboarding') === 'true')) {
      setIsOnboardedSubscribing(true)
    } else {
      setIsOnboardedSubscribing(false)
    }
  }, [searchParams])

  return (
    <>
      {/* New free-restricted user */}
      {(user.details.subscription.tier === 'free-restricted' &&
        journeys.length === 0 &&
        !hasLimitedValiditySubscription(user)) &&
        (
          <FreeRestrictedNewUserOnboardingModal />
        )}

      {(user.details.subscription.tier === 'premium' || hasLimitedValiditySubscription(user)) &&
        user.details.homeCountry === undefined &&
        !isLegacyUser(user) &&
        (
          <RestrictedPurchasedSubscriptionOnboardingModal />
        )}

      {/* Legacy user subscription purchased or onboarded free-restricted purchasing a subscription */}
      {isOnboardedSubscribing &&
        (user.details.subscription.tier === 'premium' || hasLimitedValiditySubscription(user)) &&
        user.details.homeCountry !== undefined &&
        (
          <NewSubscriptionAcknowledgementModal />
        )}


      {/* Legacy onboarding */}
      {user.details.subscription.tier === 'free-standard' &&
        user.details.homeCountry === undefined &&
        isLegacyUser(user) &&
        (
          <LegacyNewFreeUserOnboardingView />
        )}
    </>
  )
}